import React, { useCallback, useEffect, useMemo } from 'react';
import {
  Button,
  Card,
  Form,
  FormField,
  FormGroup,
  Grid,
  Message,
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
  Radio,
} from 'semantic-ui-react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  change as changeAction,
  clearSubmitErrors,
  Field,
  formValueSelector,
  reduxForm,
  submit,
  touch,
} from 'redux-form';
import validator from 'validator';
import { addDays } from 'date-fns';
import { get } from 'lodash';
import {
  setCouponAssignMerchantsModalShown,
  setCouponBusinessIdValidationResult,
  setCouponEditorErrorMessage,
  setEditorModalFormDirty,
} from 'state/coupons/actions';
import {
  COUPON_ASSIGNED_MERCHANTS_FORM,
  COUPON_FORM,
  COUPON_QUANTITY,
  MERCHANT_QUANTITY,
  MONTHLY_SUBSCRIPTION_LENGTH,
  ANNUAL_SUBSCRIPTION_LENGTH,
} from 'constants/billingCoupon/formConstants';
import ValidatedFormInput from 'components/ValidatedFormInput';
import DatePicker from 'components/DatePicker';
import CouponAssignMerchants from 'pages/Coupons/CouponAssignMerchants';
import {
  COUPON_ADDITIONAL_MONTH_INVALID,
  COUPON_CODE_LENGTH_MUST_BE_ALPHANUMERIC_BETWEEN_1_AND_20,
  COUPON_DISCOUNT_PERCENTAGE_INVALID_AMOUNT,
  COUPON_DISCOUNT_REWARD_OR_SUBSCRIPTION_EXTENSION_IS_REQUIRED,
  COUPON_EXPIRATION_DATE_REQUIRED,
  COUPON_MAX_DISCOUNT_INVALID_AMOUNT,
  COUPON_MERCHANTS_UNASSIGNED,
  COUPON_QUANTITY_INVALID,
} from 'constants/billingCoupon/validationConstants';

const percentageValidation = value => {
  const parsed = parseFloat((value || '').replace(',', '.'));
  return parsed && parsed >= 1 && parsed <= 100
    ? undefined
    : COUPON_DISCOUNT_PERCENTAGE_INVALID_AMOUNT;
};
const couponQuantityValidation = value =>
  Number.isInteger(value) && value >= 1 && value <= 1000000
    ? undefined
    : COUPON_QUANTITY_INVALID;
export const couponCodeValidation = value => {
  if (
    !value ||
    validator.isEmpty(value.trim()) ||
    !/^[a-z0-9]{1,20}$/i.test(value)
  ) {
    return COUPON_CODE_LENGTH_MUST_BE_ALPHANUMERIC_BETWEEN_1_AND_20;
  }
  return undefined;
};

const expirationDateRequiredValidation = value =>
  value ? undefined : COUPON_EXPIRATION_DATE_REQUIRED;

const parseIntegerValue = str => {
  if (str.trim() === '') {
    return null;
  }
  return parseInt(str, 10);
};

const normalizeFloatValue = (value, previousValue) => {
  const numberRegex = /^(?:[0-9]+(?:,[0-9]*)?)?$/;
  return numberRegex.test(value) ? value : previousValue;
};

const maxDiscountValidation = value =>
  !Number.isInteger(value) || (value >= 1000 && value <= 100000000)
    ? undefined
    : COUPON_MAX_DISCOUNT_INVALID_AMOUNT;

const additionalMonthValidation = value =>
  Number.isInteger(value) && value >= 1 && value <= 12
    ? undefined
    : COUPON_ADDITIONAL_MONTH_INVALID;

// no need to filter as it's already performed on CouponAssignMerchants
const countMerchantsAssigned = businessIds => (businessIds || []).length;

const onValidate = (values, dispatch) => {
  dispatch(setCouponEditorErrorMessage(null));
  const errors = {
    code: couponCodeValidation(values.code),
    expiration: expirationDateRequiredValidation(values.expiration),
  };

  if (values.discountReward) {
    errors.amount = percentageValidation(values.amount);
    errors.maxDiscount = maxDiscountValidation(values.maxDiscount);
  }

  if (values.subscriptionExtension) {
    errors.additionalMonth = additionalMonthValidation(values.additionalMonth);
  }

  if (!values.discountReward && !values.subscriptionExtension) {
    // eslint-disable-next-line no-underscore-dangle
    errors._error = [
      COUPON_DISCOUNT_REWARD_OR_SUBSCRIPTION_EXTENSION_IS_REQUIRED,
    ];
  }

  if (values.quotaType === COUPON_QUANTITY) {
    errors.quantity = couponQuantityValidation(values.quantity);
  } else if (values.quotaType === MERCHANT_QUANTITY) {
    const counted = countMerchantsAssigned(values.businessIds);
    errors.businessIds = counted > 0 ? undefined : COUPON_MERCHANTS_UNASSIGNED;
  }

  if (Object.values(errors).some(e => e !== undefined)) {
    const keys = Object.entries(errors)
      .filter(e => e[1] !== undefined)
      .map(e => e[0]);
    dispatch(touch(COUPON_FORM, ...keys));
    return Promise.reject(errors);
  }
  return Promise.resolve();
};

const renderErrorText = ({ meta: { error, touched } }) => {
  const errorContent = touched && !!error && error;
  return <span style={{ color: 'red' }}>{errorContent}</span>;
};

renderErrorText.propTypes = {
  meta: PropTypes.shape({
    error: PropTypes.string,
    touched: PropTypes.bool,
  }).isRequired,
};

function CouponEditorModal(props) {
  const {
    open,
    onClose,
    handleSubmit,
    errorMessage,
    isLoading,
    change,
    asyncValidating,
    error,
    assignMerchantsModalShown,
    businessIdValidationResult,
  } = props;
  const formSelector = formValueSelector(COUPON_FORM);
  const dispatch = useDispatch();
  const subscriptionLength = useSelector(state =>
    formSelector(state, 'subscriptionLength')
  );
  useEffect(() => {
    if (!subscriptionLength) {
      change('subscriptionLength', ANNUAL_SUBSCRIPTION_LENGTH);
    }
  }, [change, subscriptionLength]);
  const handleSubscriptionLengthChange = useCallback(
    (e, { value }) => change('subscriptionLength', value),
    [change]
  );
  const discountRewardEnabled = useSelector(state =>
    formSelector(state, 'discountReward')
  );
  useEffect(() => {
    if (!discountRewardEnabled) {
      change('amount', undefined);
      change('maxDiscount', undefined);
    }
  }, [change, discountRewardEnabled]);

  const subscriptionExtensionEnabled = useSelector(state =>
    formSelector(state, 'subscriptionExtension')
  );
  useEffect(() => {
    if (!subscriptionExtensionEnabled) {
      change('additionalMonth', undefined);
    }
  }, [change, subscriptionExtensionEnabled]);

  const quotaType = useSelector(state => formSelector(state, 'quotaType'));
  useEffect(() => {
    if (quotaType !== COUPON_QUANTITY) {
      change('quantity', undefined);
    }
    if (quotaType !== MERCHANT_QUANTITY) {
      change('businessIds', undefined);
      dispatch(setCouponBusinessIdValidationResult(null));
    }
  }, [change, dispatch, quotaType]);
  const handleQuotaTypeChange = useCallback(
    (e, { value }) => change('quotaType', value),
    [change]
  );
  const onSubmitButtonClicked = useCallback(() => {
    dispatch(clearSubmitErrors(COUPON_FORM));
    dispatch(setEditorModalFormDirty(false));
    dispatch(setCouponBusinessIdValidationResult(null));
    dispatch(setCouponEditorErrorMessage(null));
    dispatch(submit(COUPON_FORM));
  }, [dispatch]);

  const isFormDirty = useSelector(
    state => state.billingCoupon.editorModalFormDirty
  );

  const isSubmitButtonDisabled = () =>
    isLoading || asyncValidating || !isFormDirty;

  const combinedErrors = [...(errorMessage || []), ...(error || [])];

  const issuerId = useSelector(state =>
    get(state, 'session.currentUser.id', null)
  );
  useEffect(() => {
    change('issuerId', issuerId);
  }, [change, issuerId]);

  const onAssignMerchantsModalClosed = useCallback(
    () => dispatch(setCouponAssignMerchantsModalShown(false)),
    [dispatch]
  );

  const businessIdsInForm = useSelector(
    state => formSelector(state, 'businessIds') || []
  );
  const merchantsAssignedCount = useMemo(
    () => countMerchantsAssigned(businessIdsInForm),
    [businessIdsInForm]
  );
  const onShowAssignMerchantsModal = useCallback(() => {
    dispatch(
      changeAction(
        COUPON_ASSIGNED_MERCHANTS_FORM,
        'businessIds',
        businessIdsInForm.map(b => b.toString())
      )
    );
    dispatch(setCouponAssignMerchantsModalShown(true));
  }, [dispatch, businessIdsInForm]);

  return (
    <Modal open={open} onClose={onClose}>
      <ModalHeader>Create New Coupon</ModalHeader>
      <ModalContent>
        <Message negative hidden={combinedErrors.length === 0}>
          <Message.Header>Failed to add coupon</Message.Header>
          <ul>
            {combinedErrors.map(e => (
              <li>{e}</li>
            ))}
          </ul>
        </Message>
        <Form onSubmit={handleSubmit} loading={isLoading}>
          <FormGroup widths="equal">
            <FormField>
              <label htmlFor="coupon-code">Coupon Code</label>
              <Field
                id="coupon-code"
                name="code"
                component={ValidatedFormInput}
              />
            </FormField>
            <FormField>
              <label htmlFor="coupon-expiration">Expiration Date</label>
              <Field
                id="coupon-expiration"
                name="expiration"
                component={({ input, meta }) => (
                  <DatePicker
                    onChange={input.onChange}
                    selected={input.value}
                    minDate={addDays(new Date(), 1)}
                    customInput={
                      <ValidatedFormInput meta={meta} input={input} />
                    }
                  />
                )}
              />
            </FormField>
          </FormGroup>
          <p>Subscription Length</p>
          <Card fluid>
            <Card.Content>
              <Grid columns={2}>
                <Grid.Row>
                  <Grid.Column width={8}>
                    <Radio
                      label={
                        <label>
                          <strong>Annual</strong>
                        </label>
                      }
                      name="subscriptionLength"
                      value={ANNUAL_SUBSCRIPTION_LENGTH}
                      checked={
                        subscriptionLength === ANNUAL_SUBSCRIPTION_LENGTH
                      }
                      onChange={handleSubscriptionLengthChange}
                    />
                  </Grid.Column>
                  <Grid.Column width={4}>
                    <Radio
                      label={
                        <label>
                          <strong>Monthly</strong>
                        </label>
                      }
                      name="subscriptionLength"
                      value={MONTHLY_SUBSCRIPTION_LENGTH}
                      checked={
                        subscriptionLength === MONTHLY_SUBSCRIPTION_LENGTH
                      }
                      onChange={handleSubscriptionLengthChange}
                    />
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Card.Content>
          </Card>
          <p>Reward</p>
          <Card fluid>
            <Card.Content>
              <Form.Checkbox
                toggle
                label="Discount"
                checked={discountRewardEnabled}
                onChange={() =>
                  change('discountReward', !discountRewardEnabled)
                }
              />
            </Card.Content>
            <Card.Content>
              <Grid columns={2}>
                <Grid.Row>
                  <Grid.Column width={8}>
                    <FormField>
                      <label htmlFor="coupon-percentage">Percentage</label>
                      <Field
                        id="coupon-percentage"
                        name="amount"
                        component={ValidatedFormInput}
                        normalize={normalizeFloatValue}
                        icon="percent"
                        disabled={!discountRewardEnabled}
                      />
                    </FormField>
                  </Grid.Column>
                  <Grid.Column width={8}>
                    <FormField>
                      <label htmlFor="coupon-maxdiscount">
                        Maximum Discount
                      </label>
                      <Field
                        id="coupon-maxdiscount"
                        name="maxDiscount"
                        component={ValidatedFormInput}
                        parse={parseIntegerValue}
                        label={{ basic: true, content: 'Rp' }}
                        labelPosition="left"
                        disabled={!discountRewardEnabled}
                        placeholder="(Optional)"
                      />
                    </FormField>
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Card.Content>
          </Card>
          <Card fluid>
            <Card.Content>
              <Form.Checkbox
                toggle
                label="Subscription Extension"
                checked={subscriptionExtensionEnabled}
                onChange={() =>
                  change('subscriptionExtension', !subscriptionExtensionEnabled)
                }
              />
            </Card.Content>
            <Card.Content>
              <Grid columns={1}>
                <Grid.Row>
                  <Grid.Column width={8}>
                    <FormField>
                      <label htmlFor="coupon-additional-month">
                        Additional Month
                      </label>
                      <Field
                        id="coupon-additional-month"
                        name="additionalMonth"
                        component={ValidatedFormInput}
                        parse={parseIntegerValue}
                        disabled={!subscriptionExtensionEnabled}
                        label={{ basic: true, content: 'Months' }}
                        labelPosition="right"
                      />
                    </FormField>
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Card.Content>
          </Card>
          <p>Quota</p>
          <Card fluid>
            <Card.Content>
              <Grid columns={2}>
                <Grid.Row>
                  <Grid.Column width={12}>
                    <Radio
                      label={
                        <label>
                          <strong>Coupon Quantity</strong>
                          <br />
                          All merchants are able to use this coupon
                        </label>
                      }
                      name="quotaType"
                      value={COUPON_QUANTITY}
                      checked={quotaType === COUPON_QUANTITY}
                      onChange={handleQuotaTypeChange}
                    />
                  </Grid.Column>
                  <Grid.Column width={4}>
                    <Field
                      id="coupon-quantity"
                      name="quantity"
                      component={ValidatedFormInput}
                      parse={parseIntegerValue}
                      disabled={quotaType !== COUPON_QUANTITY}
                    />
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Card.Content>
            <Card.Content>
              <Grid columns={2}>
                <Grid.Row>
                  <Grid.Column width={8}>
                    <Radio
                      label={
                        <label>
                          <strong>Merchant Quantity</strong>
                          <br />
                          Only specific merchants are able to use this coupon
                        </label>
                      }
                      name="quotaType"
                      value={MERCHANT_QUANTITY}
                      checked={quotaType === MERCHANT_QUANTITY}
                      onChange={handleQuotaTypeChange}
                    />
                  </Grid.Column>
                  <Grid.Column width={4}>
                    {merchantsAssignedCount} merchants assigned
                    <br />
                    <Field name="businessIds" component={renderErrorText} />
                  </Grid.Column>
                  <Grid.Column width={4}>
                    <CouponAssignMerchants
                      open={assignMerchantsModalShown}
                      onClose={onAssignMerchantsModalClosed}
                      businessIdValidationResult={businessIdValidationResult}
                    />
                    <Button
                      type="button"
                      basic
                      positive
                      disabled={isLoading || quotaType !== MERCHANT_QUANTITY}
                      onClick={onShowAssignMerchantsModal}
                    >
                      Assign Merchant
                    </Button>
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Card.Content>
          </Card>
        </Form>
      </ModalContent>
      <ModalActions>
        <Button basic onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          positive
          onClick={onSubmitButtonClicked}
          loading={isLoading}
          disabled={isSubmitButtonDisabled()}
        >
          Submit
        </Button>
      </ModalActions>
    </Modal>
  );
}

CouponEditorModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  errorMessage: PropTypes.arrayOf(PropTypes.string),
  isLoading: PropTypes.bool.isRequired,
  change: PropTypes.func.isRequired,
  asyncValidating: PropTypes.oneOfType([PropTypes.bool, PropTypes.string])
    .isRequired,
  error: PropTypes.arrayOf(PropTypes.string),
  assignMerchantsModalShown: PropTypes.bool.isRequired,
  businessIdValidationResult: PropTypes.object,
};

CouponEditorModal.defaultProps = {
  errorMessage: [],
  error: undefined,
  businessIdValidationResult: null,
};

export default reduxForm({
  form: COUPON_FORM,
  asyncValidate: onValidate,
  asyncBlurFields: [],
  onChange: (values, dispatch) => {
    dispatch(setEditorModalFormDirty(true));
  },
})(CouponEditorModal);
